// File: src/pages/IntakeFormPage.js

// --- 1. ИМПОРТЫ ---
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import { useUiLanguage } from '../context/UiLanguageContext';
import './IntakeFormPage.css'; // Подключаем стили для этой страницы.

// --- 2. ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ---

// Компонент для отображения индикатора загрузки на всю страницу.
const Loader = () => {
    const { t } = useUiLanguage();
    return (
        <div className="loader-overlay">
            <div className="loader"></div>
            <p>{t('practice.intake.loading', 'Loading Form...')}</p>
        </div>
    );
};

// Компонент для модального окна подтверждения финальной отправки.
const ConfirmationModal = ({ isOpen, onCancel, onConfirm, isLoading }) => {
    const { t } = useUiLanguage();
    if (!isOpen) return null;
    return (
        <div className="modal-overlay">
            <div className="modal-content fade-in">
                <div className="modal-header">
                    <h2>{t('practice.intake.modal_title', 'Confirm Submission')}</h2>
                </div>
                <div className="modal-body">
                    <p>{t('practice.intake.modal_body', 'Are you sure you want to finalize and submit this case for AI analysis? This action cannot be undone.')}</p>
                </div>
                <div className="modal-footer">
                    <button onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
                        {t('practice.intake.modal_cancel', 'Cancel')}
                    </button>
                    <button onClick={onConfirm} className="btn btn-primary" disabled={isLoading}>
                        {isLoading ? t('practice.intake.modal_analyzing', 'Analyzing...') : t('practice.intake.modal_confirm', 'Confirm & Analyze')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- 3. ОСНОВНОЙ КОМПОНЕНТ СТРАНИЦЫ ---
const IntakeFormPage = () => {
    const { t } = useUiLanguage();
    // --- 4. ХУКИ И СОСТОЯНИЕ ---
    const { caseId } = useParams(); // Получаем ID дела из URL.
    const navigate = useNavigate(); // Хук для программной навигации.

    // Состояние для хранения структуры формы (модули и вопросы).
    const [modules, setModules] = useState([]);
    // Состояние для хранения ответов пользователя в виде { question_id: answer_text }.
    const [responses, setResponses] = useState({});
    // Индекс текущего отображаемого модуля.
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    // Состояние для отслеживания общей загрузки данных.
    const [isLoading, setIsLoading] = useState(true);
    // Состояние для отслеживания процесса сохранения ответов.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Процент завершенности формы.
    const [completionPercentage, setCompletionPercentage] = useState(0);
    // Состояние для управления видимостью модального окна.
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

    // --- 5. ЗАГРУЗКА ДАННЫХ ---
    // Функция для асинхронной загрузки всех необходимых данных для формы.
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Параллельно запрашиваем структуру вопросов, сохраненные ответы и процент выполнения.
            const [questionsData, responsesData, completionData] = await Promise.all([
                apiService.getIntakeQuestions(),
                apiService.getIntakeResponses(caseId),
                apiService.getIntakeCompletion(caseId),
            ]);
            
            // Преобразуем структуру вопросов из объекта в массив для удобной итерации.
            setModules(Object.entries(questionsData).map(([name, questions]) => ({ name, questions })));

            // Преобразуем массив ответов в объект (карту) для быстрого доступа по ID вопроса.
            const responsesMap = responsesData.reduce((acc, resp) => {
                acc[resp.question_id] = resp.answer_text || '';
                return acc;
            }, {});
            setResponses(responsesMap);
            
            // Устанавливаем процент завершенности.
            setCompletionPercentage(completionData.percentage || 0);

        } catch (error) {
            toast.error(t('practice.intake.load_failed', 'Failed to load intake form data. Redirecting...'));
            console.error("Error fetching data:", error);
            setTimeout(() => navigate('/dashboard'), 3000); // В случае ошибки перенаправляем пользователя.
        } finally {
            setIsLoading(false);
        }
    }, [caseId, navigate, t]);

    // Вызываем функцию загрузки данных один раз при монтировании компонента.
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- 6. ОБРАБОТЧИКИ СОБЫТИЙ ---

    // Обновляет состояние ответов при вводе текста пользователем.
    const handleInputChange = (questionId, value) => {
        setResponses(prev => ({ ...prev, [questionId]: value }));
    };

    // Сохраняет ответы текущего модуля на сервере.
    const saveCurrentModuleResponses = async () => {
        setIsSubmitting(true);
        const currentModule = modules[currentModuleIndex];
        if (!currentModule) {
            setIsSubmitting(false);
            return;
        }

        // Собираем только те ответы, которые относятся к текущему модулю и не являются пустыми.
        const responsesToSubmit = currentModule.questions
            .filter(q => responses[q.id] !== undefined && responses[q.id] !== '')
            .map(q => ({
                question_id: q.id,
                answer_text: responses[q.id]
            }));

        if (responsesToSubmit.length > 0) {
            try {
                // Формируем правильный объект для отправки на API.
                const submissionData = {
                    case_id: parseInt(caseId),
                    responses: responsesToSubmit
                };
                await apiService.submitIntakeForm(submissionData);
                toast.success(`Module '${currentModule.name}' saved!`);
                
                // После успешного сохранения обновляем процент выполнения.
                const completionRes = await apiService.getIntakeCompletion(caseId);
                setCompletionPercentage(completionRes.percentage || 0);
            } catch (error) {
                toast.error(t('practice.intake.save_failed', 'Failed to save responses.'));
                console.error("Error saving responses:", error);
            }
        }
        setIsSubmitting(false);
    };
    
    // Сохраняет текущий модуль и переходит к следующему.
    const handleNext = async () => {
        await saveCurrentModuleResponses();
        if (currentModuleIndex < modules.length - 1) {
            setCurrentModuleIndex(prev => prev + 1);
        }
    };

    // Переходит к предыдущему модулю (без сохранения).
    const handlePrev = () => {
        if (currentModuleIndex > 0) {
            setCurrentModuleIndex(prev => prev - 1);
        }
    };

    // Сохраняет последний модуль и открывает модальное окно для финального подтверждения.
    const handleFinalSubmit = async () => {
        await saveCurrentModuleResponses();
        setConfirmModalOpen(true);
    };

    // Подтверждает и запускает анализ дела.
    const confirmAnalysis = async () => {
        setIsLoading(true);
        const analysisToast = toast.loading(t('practice.intake.modal_analyzing', 'Analyzing...'));
        try {
            await apiService.analyzeCaseFromIntake(caseId);
            toast.success(t('practice.intake.analysis_started', 'Case analysis started. Redirecting to Case Details...'), { id: analysisToast });
            navigate(`/cases/${caseId}/analysis`); // Перенаправляем на страницу с результатами анализа.
        } catch (error) {
            toast.error(t('practice.intake.finalize_failed', 'Failed to start case analysis.'), { id: analysisToast });
            console.error("Error finalizing submission:", error);
        } finally {
            setIsLoading(false);
            setConfirmModalOpen(false);
        }
    };

    // --- 7. УСЛОВНЫЙ РЕНДЕРИНГ И JSX ---
    if (isLoading && !isConfirmModalOpen) {
        return <Loader />;
    }

    const currentModule = modules[currentModuleIndex];
    const isLastModule = currentModuleIndex === modules.length - 1;

    return (
        <div className="intake-form-container card fade-in">
             <ConfirmationModal 
                isOpen={isConfirmModalOpen}
                onCancel={() => setConfirmModalOpen(false)}
                onConfirm={confirmAnalysis}
                isLoading={isLoading}
            />
            <header className="intake-form-header">
                <h2>{t('practice.intake.title', { caseId })}</h2>
                <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${completionPercentage}%` }}></div>
                    <span>{t('practice.intake.percent_complete', { percent: Math.round(completionPercentage) })}</span>
                </div>
            </header>

            {currentModule ? (
                <div className="module-container">
                    <h3 data-user-content="true">{currentModule.name}</h3>
                    <form onSubmit={(e) => e.preventDefault()} className="grid grid-cols-1">
                        {currentModule.questions.map(q => (
                            <div key={q.id} className="form-group">
                                <label htmlFor={`q-${q.id}`} className="form-label" data-user-content="true">{q.question_text}</label>
                                {q.question_type === 'TEXTAREA' ? (
                                    <textarea
                                        id={`q-${q.id}`}
                                        className="form-input"
                                        value={responses[q.id] || ''}
                                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                                        rows="4"
                                    />
                                ) : (
                                    <input
                                        id={`q-${q.id}`}
                                        className="form-input"
                                        type={q.question_type === 'DATE' ? 'date' : 'text'}
                                        value={responses[q.id] || ''}
                                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                                    />
                                )}
                            </div>
                        ))}
                    </form>
                </div>
            ) : (
                <p>{t('practice.intake.no_questions', 'No questions found for this case type.')}</p>
            )}

            <footer className="navigation-buttons">
                <button onClick={handlePrev} className="btn btn-secondary" disabled={currentModuleIndex === 0 || isSubmitting}>
                    {t('practice.intake.btn_previous', 'Previous')}
                </button>
                {!isLastModule && (
                    <button onClick={handleNext} className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? t('practice.intake.saving', 'Saving...') : t('practice.intake.btn_save_next', 'Save and Next')}
                    </button>
                )}
                {isLastModule && (
                    <button onClick={handleFinalSubmit} className="btn btn-primary final-submit" disabled={isSubmitting}>
                        {isSubmitting ? t('practice.intake.saving', 'Saving...') : t('practice.intake.btn_finish_analyze', 'Finish & Analyze Case')}
                    </button>
                )}
            </footer>
        </div>
    );
};

// --- 8. ЭКСПОРТ ---
export default IntakeFormPage;
